import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  CheckCircle, 
  Bot,
  Zap,
  Lightbulb,
  Users,
  Workflow,
  Brain,
  Target,
  Shield,
  BarChart3
} from 'lucide-react';
import IntakeModal from '../components/IntakeModal';

const Home = () => {
  const [isIntakeOpen, setIsIntakeOpen] = useState(false);
  const [intakeType, setIntakeType] = useState('client-zero');

  const openIntake = (type) => {
    setIntakeType(type);
    setIsIntakeOpen(true);
  };

  // Pain points - where AI belongs
  const painPoints = [
    "Customers waiting for answers.",
    "Leads waiting for follow-up.",
    "Employees copying information between systems.",
    "Teams repeatedly answering the same questions.",
    "Managers chasing updates.",
    "Appointments requiring manual coordination.",
    "Employees searching for information.",
    "Administrative work consuming hours every week."
  ];

  // 6-step process
  const processSteps = [
    { num: "01", title: "DISCOVER", description: "We examine your workflows, customer journeys and repetitive processes to identify where AI and automation could create value." },
    { num: "02", title: "PRIORITIZE", description: "We determine which opportunities have the strongest combination of business value, feasibility and measurable impact." },
    { num: "03", title: "DESIGN", description: "We redesign the workflow around people, AI, automation and your existing technology." },
    { num: "04", title: "IMPLEMENT", description: "We configure and integrate the appropriate solution." },
    { num: "05", title: "MEASURE", description: "We evaluate agreed business outcomes such as response time, workload reduction, process efficiency, customer experience or opportunity capture." },
    { num: "06", title: "SCALE", description: "Once the business case has been demonstrated, we identify the next workflow worth transforming." }
  ];

  // Three ways to help
  const serviceOfferings = [
    {
      icon: Lightbulb,
      title: "AI Strategy & Readiness",
      tagline: "Know where to start.",
      description: "For organizations that know AI matters but aren't sure where it belongs. We help assess workflows, systems, opportunities, implementation considerations and business priorities to develop a practical AI roadmap.",
      ideal: "\"How should we actually use AI?\"",
      cta: "Explore AI Strategy",
      link: "/services"
    },
    {
      icon: Zap,
      title: "AI Automation & Implementation",
      tagline: "Turn repetitive work into intelligent workflows.",
      description: "We help design and implement automation connecting your people, processes, approved AI technologies and existing business systems. From intake and customer communication to workflow coordination and internal processes, we focus on automation that solves real operational problems.",
      ideal: "\"How can we stop doing this manually?\"",
      cta: "Explore AI Automation",
      link: "/services"
    },
    {
      icon: Bot,
      title: "AI Employees",
      tagline: "Give repetitive work to AI. Give valuable time back to people.",
      description: "Deploy AI employees such as AVA to support defined customer and operational workflows. Start with one job. Measure the result. Expand when the business case is proven.",
      ideal: "\"What if AI could actually do some of this work?\"",
      cta: "Meet AVA",
      link: "/altam"
    }
  ];

  // Business case questions
  const businessCaseQuestions = [
    "How much employee time does this process consume?",
    "How frequently does it happen?",
    "What does that time cost the business?",
    "Where are delays affecting customers?",
    "Are opportunities being lost because people can't respond quickly enough?",
    "What measurable improvement would make the investment worthwhile?"
  ];

  // Pecaro considerations
  const pecaroConsiderations = [
    { icon: Users, title: "People", description: "Who performs the work today?" },
    { icon: Workflow, title: "Process", description: "How does the work actually happen?" },
    { icon: Brain, title: "Technology", description: "Which systems need to participate?" },
    { icon: Target, title: "Customer Experience", description: "How will the change affect customers?" },
    { icon: Shield, title: "Risk", description: "Where should permissions, controls and human oversight exist?" },
    { icon: BarChart3, title: "Business Value", description: "What measurable outcome should the investment create?" }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 bg-gradient-to-br from-[#F5F5F5] via-white to-[#F5F5F5]"></div>
        <div className="absolute top-20 right-10 w-72 h-72 bg-[#227AFF]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#227AFF]/5 rounded-full blur-3xl"></div>

        <div className="container mx-auto px-4 py-20 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="mb-6 animate-fade-in-up">
              AI Shouldn't Add More Complexity to Your Business.{' '}
              <span className="text-gradient">It Should Give You Time Back.</span>
            </h1>
            
            <p className="text-xl text-gray-600 mb-6 max-w-3xl mx-auto animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              Pecaro Consulting helps businesses use AI to eliminate repetitive work, improve customer experiences and build smarter operations.
            </p>
            
            <p className="text-lg text-gray-500 mb-6 animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
              From AI strategy and intelligent automation to AI employees like <span className="text-[#227AFF] font-semibold">AVA™</span>, we help you identify where AI can create measurable value — then implement it.
            </p>
            
            <p className="text-lg text-gray-700 font-medium mb-10 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              Start with one business problem. We'll help you build the solution.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
              <Link to="/book-consultation" className="btn btn-primary text-lg px-8" data-testid="hero-cta-primary">
                Book Your AI Strategy Session
              </Link>
              <button 
                onClick={() => openIntake('meet-ava')} 
                className="btn btn-glass text-lg px-8 inline-flex items-center gap-2" 
                data-testid="hero-cta-secondary"
              >
                Meet AVA <ArrowRight className="h-4 w-4" />
              </button>
            </div>
            
            <div className="mt-12 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
              <p className="text-sm text-gray-500 font-medium">
                AI Strategy • AI Automation • AI Employees • Digital Transformation
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Where AI Belongs Section */}
      <section className="section section-light">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="mb-4">Your Business Is Already Telling You Where AI Belongs.</h2>
              <div className="accent-line"></div>
            </div>

            <p className="text-xl text-gray-700 text-center mb-4">
              You probably don't need another piece of software.
            </p>
            <p className="text-lg text-gray-600 text-center mb-10">
              You need the systems you already have — and the people who use them — to work better together.
            </p>

            <div className="bg-white rounded-2xl p-8 shadow-lg mb-10">
              <p className="text-lg font-semibold text-gray-800 mb-6 text-center">Look at where your team spends its time.</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {painPoints.map((point, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-[#227AFF] rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">{point}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-r from-[#227AFF]/5 to-[#007BFF]/5 rounded-xl p-6 text-center">
              <p className="text-gray-700 mb-2">These aren't simply operational annoyances.</p>
              <p className="text-xl font-semibold text-[#0A1538] mb-4">They're opportunities for intelligent automation.</p>
              <p className="text-[#227AFF] font-medium mb-6">Pecaro helps you find them.</p>
              <Link to="/book-consultation" className="btn btn-primary inline-flex items-center gap-2">
                Discover What You Could Automate <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Don't Start With AI Section */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="mb-4">Don't Start With AI.<br />Start With the Business Problem.</h2>
              <div className="accent-line"></div>
              <p className="text-lg text-gray-600 mt-6">
                AI transformation shouldn't begin with choosing technology.
              </p>
              <p className="text-lg text-gray-600">
                It should begin with a question:
              </p>
              <p className="text-2xl font-semibold text-[#0A1538] mt-4">
                Where is your business losing time, money or opportunities?
              </p>
              <p className="text-lg text-[#227AFF] font-medium mt-4">That's where Pecaro starts.</p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
              {processSteps.map((step, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-[#227AFF]">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-[#227AFF] font-bold text-lg">{step.num}</span>
                    <span className="text-gray-400">—</span>
                    <h4 className="font-semibold text-gray-800">{step.title}</h4>
                  </div>
                  <p className="text-gray-600 text-sm">{step.description}</p>
                </div>
              ))}
            </div>

            <div className="bg-[#0A1538] rounded-xl p-6 text-center">
              <p className="text-xl font-semibold text-white mb-4">Start small. Prove value. Scale intelligently.</p>
              <Link to="/book-consultation" className="btn btn-accent">
                Book an AI Strategy Session
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Meet AVA Teaser - Brief Section */}
      <section className="section section-dark">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Bot className="h-5 w-5 text-[#227AFF]" />
              <span className="text-[#227AFF] font-semibold">Introducing AVA™</span>
            </div>
            
            <h2 className="text-white mb-4">What If AI Could Actually Do Some of This Work?</h2>
            
            <p className="text-xl text-gray-300 mb-6 max-w-2xl mx-auto">
              AVA is an AI employee powered by <span className="text-[#227AFF] font-semibold">ALTAM™</span> — designed to handle repetitive customer and operational work while keeping your people in control.
            </p>

            <div className="grid sm:grid-cols-3 gap-4 mb-8 max-w-2xl mx-auto">
              {['Customer Service', 'Lead Qualification', 'Appointment Scheduling'].map((item, index) => (
                <div key={index} className="bg-white/5 backdrop-blur-sm rounded-lg px-4 py-3 border border-white/10">
                  <span className="text-white text-sm">{item}</span>
                </div>
              ))}
            </div>

            <p className="text-gray-400 mb-8">
              Give repetitive work to AI. Give valuable time back to people.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button onClick={() => openIntake('meet-ava')} className="btn btn-accent inline-flex items-center gap-2">
                Meet AVA & Explore ALTAM <ArrowRight className="h-4 w-4" />
              </button>
              <button onClick={() => openIntake('client-zero')} className="btn btn-glass inline-flex items-center gap-2" data-testid="apply-client-zero-btn">
                Apply for Client Zero <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Three Ways Pecaro Can Help Section */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="mb-4">Three Ways Pecaro Can Help</h2>
              <div className="accent-line"></div>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {serviceOfferings.map((service, index) => {
                const IconComponent = service.icon;
                return (
                  <div key={index} className="bg-white rounded-2xl p-6 shadow-lg flex flex-col h-full">
                    <div className="p-3 rounded-lg bg-[#227AFF]/10 w-fit mb-4">
                      <IconComponent className="h-8 w-8 text-[#227AFF]" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 mb-1">{service.title}</h3>
                    <p className="text-[#227AFF] font-medium mb-4">{service.tagline}</p>
                    <p className="text-gray-600 text-sm mb-4 flex-grow">{service.description}</p>
                    <div className="bg-gray-50 rounded-lg p-3 mb-4">
                      <p className="text-sm text-gray-500">Ideal for businesses asking:</p>
                      <p className="text-gray-700 font-medium italic">{service.ideal}</p>
                    </div>
                    <Link to={service.link} className="btn btn-primary w-full text-center inline-flex items-center justify-center gap-2">
                      {service.cta} <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* AI Has to Earn Its Place Section */}
      <section className="section section-light">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="mb-4">AI Has to Earn Its Place in Your Business.</h2>
              <div className="accent-line"></div>
              <p className="text-lg text-gray-600 mt-6">
                We don't recommend automation simply because something <em>can</em> be automated.
              </p>
              <p className="text-lg text-gray-700 font-semibold">The business case has to make sense.</p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
              <p className="text-gray-700 mb-6 font-medium">Before implementing AI, we look at questions such as:</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {businessCaseQuestions.map((question, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-[#227AFF] flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700">{question}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#0A1538] rounded-xl p-6 text-center">
              <p className="text-gray-300 mb-2">If the business case isn't compelling, it probably shouldn't be the first thing you automate.</p>
              <p className="text-gray-400 mb-4">AI isn't the strategy.</p>
              <p className="text-xl font-semibold text-white mb-6">Better business performance is.</p>
              <Link to="/book-consultation" className="btn btn-accent inline-flex items-center gap-2">
                Let's Find Your Highest-Value Opportunity <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Pecaro Consulting */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="mb-4">Why Pecaro Consulting?</h2>
              <div className="accent-line"></div>
            </div>

            <p className="text-xl text-gray-700 text-center mb-4">
              Technology alone doesn't transform a business.
            </p>
            <p className="text-xl font-semibold text-[#0A1538] text-center mb-8">
              Implementation does.
            </p>

            <p className="text-lg text-gray-600 text-center mb-8">
              Pecaro brings together business strategy, project delivery, digital transformation, process improvement, automation and AI implementation.
            </p>

            <p className="text-lg text-gray-700 text-center mb-8">
              That means we look beyond the AI model. <span className="font-semibold">We consider:</span>
            </p>

            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 mb-10">
              {pecaroConsiderations.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <div key={index} className="bg-white rounded-xl p-5 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <IconComponent className="h-5 w-5 text-[#227AFF]" />
                      <h4 className="font-semibold text-[#227AFF]">{item.title}</h4>
                    </div>
                    <p className="text-gray-600 text-sm">{item.description}</p>
                  </div>
                );
              })}
            </div>

            <div className="bg-gradient-to-r from-[#227AFF]/5 to-[#007BFF]/5 rounded-xl p-6 text-center">
              <p className="text-gray-700">Then we design the technology around the business.</p>
              <p className="text-xl font-semibold text-[#0A1538] mt-2">Not the other way around.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Client Zero CTA Banner */}
      <section className="section section-dark">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-[#227AFF] font-semibold mb-2">AVA Client Zero Program</p>
            <h2 className="text-white mb-4">Ready to Put Your First AI Employee to Work?</h2>
            
            <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
              We're selecting a limited number of businesses to implement AVA through our founding customer program. <span className="text-white font-semibold">$3,500 CAD</span> — one workflow, one AI employee, measurable results.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button onClick={() => openIntake('client-zero')} className="btn btn-accent inline-flex items-center gap-2" data-testid="apply-client-zero-btn">
                Apply for Client Zero <ArrowRight className="h-4 w-4" />
              </button>
              <button onClick={() => openIntake('meet-ava')} className="btn btn-glass inline-flex items-center gap-2">
                Learn More About AVA <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-[#227AFF] font-semibold mb-2">Ready to Find Your First AI Opportunity?</p>
            <h2 className="mb-4">You Don't Need a 12-Month AI Strategy to Get Started.</h2>
            
            <p className="text-xl text-gray-700 mb-8">
              You need one business problem worth solving.
            </p>
            <p className="text-lg text-gray-600 mb-4">
              Tell us where your organization is losing time, money or opportunities.
            </p>
            <p className="text-lg text-gray-700 font-semibold mb-8">
              We'll help you determine whether AI can create measurable value — and what to do next.
            </p>

            <div className="bg-gradient-to-r from-[#227AFF]/5 to-[#007BFF]/5 rounded-2xl p-8 mb-8">
              <p className="text-xl text-gray-700 mb-2">Start With the Problem.</p>
              <p className="text-2xl font-bold text-[#0A1538]">We'll Help You Build the Solution.</p>
            </div>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
              <Link to="/book-consultation" className="btn btn-primary text-lg px-8">
                Book Your AI Strategy Session
              </Link>
              <button onClick={() => openIntake('meet-ava')} className="btn btn-glass text-lg px-6 inline-flex items-center gap-2">
                Explore AVA & ALTAM <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Tagline */}
      <section className="bg-[#0A1538] py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-white font-bold text-lg mb-2">Pecaro Consulting Inc.</p>
            <p className="text-gray-400 text-sm mb-4">
              AI Strategy • AI Automation • AI Employees • Digital Transformation
            </p>
            <p className="text-[#227AFF] text-sm font-medium">
              Powered by human expertise. Amplified by AI.
            </p>
          </div>
        </div>
      </section>

      {/* Intake Modal */}
      <IntakeModal 
        isOpen={isIntakeOpen} 
        onClose={() => setIsIntakeOpen(false)} 
        type={intakeType}
      />
    </div>
  );
};

export default Home;
